<script>
import { get } from "lodash";
import vSelect from "vue-select";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

export default {
  name: "GeoSelector",
  components: {
    vSelect,
  },
  props: {
    selectorType: { // area, district, assembly
      type: String,
      default: null,
    },
    geoFilterValues: {
      type: Object,
      default: () => ({}),
    },
    multiple: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedGeo: null,
      selectOptions: [],
      allAreaOptions: [],
    };
  },
  computed: {
    placeholder() {
      return `Search ${this.selectorType}`;
    },
    optionsToUse() {
      // For area, use preloaded options and filter by region on client
      if (this.selectorType === 'area') {
        const regionId = this.geoFilterValues && this.geoFilterValues.region;
        if (!regionId) return this.allAreaOptions;
        return this.allAreaOptions.filter(opt => opt.region_id === regionId);
      }
      // For others, use fetched options
      return this.selectOptions;
    },
    selectionOptions() {
      if (!this.selectedGeo) return [];
      const selected = Array.isArray(this.selectedGeo) ? this.selectedGeo : [this.selectedGeo];
      return this.selectOptions.filter(option => selected.includes(option.value));
    },
  },
  watch: {
    selectedGeo(newVal) {
      this.$emit("input", newVal);
    },
  },
  mounted() {
    // Preload all areas once for client-side filtering
    if (this.selectorType === 'area') {
      this.fetchAllAreas();
    }
  },
  methods: {
    async fetchAllAreas() {
      try {
        const query = { limit: 100000 };
        const request = await this.useJwt().sharedService.fetchGeoAreas(query);
        const { data } = request.data;
        this.allAreaOptions = data.map(item => ({
          label: item.title,
          value: item._id,
          region_id: item.region_id || (item.region && item.region._id) || null,
        }));
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message;
        console.log("error_message", error_message);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error!",
            icon: "AlertTriangleIcon",
            variant: "danger",
            text: error_message,
          },
        });
      }
    },
    async onSearchArea(search, loading) {
      try {
        loading(true);

        // For area, do not call API on search; rely on client-side filtering
        if (this.selectorType === 'area') {
          return;
        }

        const query = {
          limit: 1000,
          search,
        };

        let request = {};
        if (this.selectorType === "area") {
          if (this.geoFilterValues.region) {
            query.region_id = this.geoFilterValues.region;
          }
          request = await this.useJwt().sharedService.fetchGeoAreas(query);
        } else if (this.selectorType === "district") {
          if (this.geoFilterValues.area) {
            query.area_id = this.geoFilterValues.area;
          }
          request = await this.useJwt().sharedService.fetchGeoDistricts(query);
        } else if (this.selectorType === "assembly") {
          if (this.geoFilterValues.district) {
            query.district_id = this.geoFilterValues.district;
          }
          request = await this.useJwt().sharedService.fetchGeoAssemblies(query);
        } else {
          throw new Error("Invalid selector type");
        }

        const { data } = request.data;

        // retain existing selection if available
        const newOptions = data.map(item => ({
          label: item.title,
          value: item._id,
        }));

        // Merge and deduplicate by value
        const mergedOptions = [...this.selectionOptions, ...newOptions];
        const uniqueOptions = [];
        const seen = new Set();
        mergedOptions.forEach(opt => {
          if (!seen.has(opt.value)) {
            seen.add(opt.value);
            uniqueOptions.push(opt);
          }
        });

        this.selectOptions = uniqueOptions;
      } catch (error) {
        const error_message =
          get(error, "response.data.message") || error.message;
        console.log("error_message", error_message);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error!",
            icon: "AlertTriangleIcon",
            variant: "danger",
            text: error_message,
          },
        });
      } finally {
        loading(false);
      }
    },
  },
};
</script>

<template>
  <v-select
    v-model="selectedGeo"
    size="small"
    :placeholder="placeholder"
    :loading="loading"
    :clearable="false"
    :options="optionsToUse"
    :reduce="(val) => val.value"
    :searchable="true"
    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
    :multiple="multiple"
    @change="$emit('input', $event)"
    @search="onSearchArea"
  />
</template>
